import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import { Box, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import useGetDataFromSelect from "src/hooks/useGetDataFromSelect";
import AutoComplete from "./AutoComplete";

export default function SearchFilterComp({
  require,
  selectList = [],
  getSearchKeyword = () => {},
  getSelectKeyword = () => {},
  getAdditionKeyword = () => {},
  width,
  placeholder,
  autoComplete,
  clear,
  minWidth,
  size = "small",
  defaultValue,
  value,
  ...props
}) {
  const [searchInput, setSearchInput] = useState("");
  const [valueAddition, setValueAddition] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [autoCompleteValue, setAutoCompleteValue] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [autoCompleteInputValue, setAutoCompleteInputValue] = useState("");

  const initializedDefaultValue = useRef(false);

  const selectedKey = selectList[selectedIndex]?.value;
  const config = autoComplete?.[selectedKey];

  const debouncedValue = useDebounce(searchInput, 500);
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const getAutoCompleteOptionLabel = useCallback(
    (option) => {
      if (!option) return "";

      if (typeof config?.getOptionLabel === "function") {
        return config.getOptionLabel(option);
      }

      if (typeof option === "string" || typeof option === "number") {
        return String(option);
      }

      return option?.name || option?.fullName || option?.label || option?.title || option?.code || "";
    },
    [config],
  );

  const { data, isLoading, setSize, isValidating, hasMore } = useGetDataFromSelect(
    config?.endpoint ?? "",
    {
      limit: config?.limit ?? 5,
      skip: config?.skip ?? 0,
      type: config?.type ?? undefined,
      searchField: config?.searchField ?? "fullName",
      searchValue: debouncedSearchValue || undefined,
      ...config?.params,
    },
    true,
  );

  const initialValue = defaultValue || value;

  useEffect(() => {
    if (initializedDefaultValue.current) {
      return;
    }

    if (!initialValue) {
      return;
    }

    const optionInData = data?.data?.find((option) => option?._id === initialValue);

    const nextValue = optionInData || {
      _id: initialValue,
    };

    setAutoCompleteValue(nextValue);

    setAutoCompleteInputValue(getAutoCompleteOptionLabel(nextValue));

    initializedDefaultValue.current = true;
  }, [initialValue, data?.data, getAutoCompleteOptionLabel]);

  useEffect(() => {
    if (!initialValue) {
      initializedDefaultValue.current = false;
      setAutoCompleteValue(null);
      setAutoCompleteInputValue("");
    }
  }, [initialValue]);

  const options = useMemo(() => {
    const opts = Array.isArray(data?.data) ? data.data.filter(Boolean) : [];

    if (autoCompleteValue && !opts.some((option) => option?._id === autoCompleteValue?._id)) {
      return [autoCompleteValue, ...opts];
    }

    return opts;
  }, [data?.data, autoCompleteValue]);

  useEffect(() => {
    if (!config?.endpoint) return;

    setSize(1);
  }, [config?.endpoint, config?.type, config?.searchField, debouncedSearchValue, setSize]);

  const debounceAddition = useDebounce(valueAddition, 500);

  useEffect(() => {
    getSearchKeyword(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    if (!!debounceAddition) {
      getAdditionKeyword?.(debounceAddition);
    }
  }, [debounceAddition]);

  const handleSelectChange = (e) => {
    const newIndex = selectList.findIndex((item) => item.id === e.target.value);

    const prevKey = selectList[selectedIndex]?.value;
    const prevConfig = autoComplete?.[prevKey];

    if (prevConfig?.state) {
      prevConfig.state(null);
    }

    setSelectedIndex(newIndex);
    getSelectKeyword(e.target.value);

    setSearchInput("");
    setValueAddition("");
    setAutoCompleteValue(null);
    setAutoCompleteInputValue("");
    setSearchValue("");
    setSize(1);

    initializedDefaultValue.current = false;
  };

  useEffect(() => {
    if (clear) {
      setSearchInput("");
      setValueAddition("");
      setAutoCompleteValue(null);
      setAutoCompleteInputValue("");
      setSearchValue("");
      setSelectedIndex(0);
      setSize(1);

      initializedDefaultValue.current = false;

      const firstKey = selectList[0]?.value;
      const firstConfig = autoComplete?.[firstKey];

      if (firstConfig?.state) {
        firstConfig.state(null);
      }

      getSearchKeyword("");
      getAdditionKeyword?.("");
      getSelectKeyword(selectList[0]?.id);
    }
  }, [clear]);

  return (
    <Box
      sx={{
        border: "1px solid var(--grey)",
        borderRadius: "6px",
        display: "flex",
        width: width || "100%",
        minWidth: !!minWidth ? minWidth : selectList?.length === 0 ? "220px" : minWidth || config?.sx?.width || "450px",
      }}
    >
      {selectList?.length > 1 && (
        <FormControl size="small">
          <Select
            sx={{
              minWidth: "180px",
              borderRadius: 0,
              "&.MuiInputBase-root fieldset": {
                border: "0 !important",
              },
              borderRight: "2px solid var(--grey)",
            }}
            value={selectList[selectedIndex]?.id}
            onChange={handleSelectChange}
          >
            {selectList.map((v) => (
              <MenuItem
                key={v?.id}
                value={v?.id}
              >
                {v?.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!config ? (
        <>
          <TextField
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {searchInput ? (
                      <IconButton
                        size="small"
                        onClick={() => setSearchInput("")}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    ) : (
                      <Search />
                    )}
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "&.MuiFormControl-root fieldset": {
                border: "0 !important",
              },
            }}
            size="small"
            placeholder={placeholder || "Tìm kiếm..."}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />

          {!!require && selectList[selectedIndex]?.value === require && (
            <TextField
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {valueAddition ? (
                        <IconButton
                          size="small"
                          onClick={() => setValueAddition("")}
                          edge="end"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      ) : (
                        <Search />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                flex: 1,
                "&.MuiFormControl-root fieldset": {
                  border: "0 !important",
                  borderLeft: "1px solid var(--grey) !important",
                },
              }}
              size="small"
              placeholder={placeholder || "Tìm kiếm..."}
              value={valueAddition}
              onChange={(e) => setValueAddition(e.target.value)}
            />
          )}
        </>
      ) : (
        <AutoComplete
          size={size}
          {...props}
          fullWidth
          loading={isLoading}
          hasMore={hasMore}
          loadingMore={isValidating && !isLoading}
          filterOptions={(opts) => opts}
          onLoadMore={() => {
            if (!isLoading && !isValidating && hasMore) {
              setSize((prev) => prev + 1);
            }
          }}
          loadMoreDelay={400}
          options={options}
          getOptionLabel={getAutoCompleteOptionLabel}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          value={autoCompleteValue}
          inputValue={autoCompleteInputValue}
          clearIcon={<Close fontSize="small" />}
          disableClearable={false}
          onChange={(_, val) => {
            setAutoCompleteValue(val);

            config?.state?.(val?._id ?? null);
            config?.getObject?.(val);

            setAutoCompleteInputValue(val ? getAutoCompleteOptionLabel(val) : "");

            setSearchValue("");
          }}
          onInputChange={(_, inputValue, reason) => {
            if (reason === "clear") {
              setAutoCompleteValue(null);
              setAutoCompleteInputValue("");
              config?.state?.(null);
              setSearchValue("");
              return;
            }

            if (reason === "input") {
              setAutoCompleteInputValue(inputValue);
              setSearchValue(inputValue);
              return;
            }

            if (reason === "reset") {
              setAutoCompleteInputValue(inputValue);
              return;
            }

            setAutoCompleteInputValue(inputValue);
          }}
          sx={{
            flex: 1,
            borderRadius: "0",
            ".MuiFormControl-root": {
              m: "0 !important",
              "&.MuiInputBase-root": {
                fieldset: {
                  border: "0 !important",
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              size={size}
              {...params}
              label={config?.placeholder ?? config?.label ?? "Tìm kiếm"}
              sx={{
                ...config?.sx,
                ...params?.sx,
              }}
            />
          )}
        />
      )}
    </Box>
  );
}
